import "katex/dist/katex.min.css";

import Latex from "react-latex-next";
import { useEffect, useRef, useState } from "react";
import type { FC } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import AnswerTutoring from "components/Answer/AnswerTutoring";

import type { UserAssignmentDetailFragment, UserAssignmentForExerciseFragment } from "api/generated";

import { useAppSelector } from "store/hooks";
import { getAssignmentResult } from "store/slices/topicPractice";
import { useTutoringContext } from "features/AssignmentTypes/Tutoring/context/TutoringContext";

import { getTextForSelfEvaluationOption } from "utils/getTextForSelfEvaluationOption";

import { useAssetUrlResolver } from "utils/hooks/useAssetUrlResolver";
import { SuccessRate } from "components/SuccessRate/SuccessRate";

interface Props {
  assignment: UserAssignmentDetailFragment | UserAssignmentForExerciseFragment;
  timeToLearn?: number;
  userAssignmentId: string;
  onNextClick?: () => void;
}

const SelfEvaluationAnalysis: FC<Props> = ({ assignment, timeToLearn, userAssignmentId, onNextClick }) => {
  const isShared = assignment.assignment?.selfEvaluation?.hasSharedSelfEvaluation === true;
  const questionsCount = assignment.assignment?.assignment?.questions.length ?? 1;

  const { t } = useTranslation(["assignments"]);
  const assignmentResult = useAppSelector(getAssignmentResult(assignment.id));
  const { tutoringId, claimEvaluation } = useTutoringContext();

  const [progress, setProgress] = useState(false);
  const loadingData = useRef(false);

  useEffect(() => {
    claimEvaluation({
      tutoringId,
      userAssignmentId,
      userAnswers: new Array(questionsCount).fill(true),
    }).catch(() => {});
  }, []);

  const handleNextClick = () => {
    setProgress(true);
  };

  useEffect(() => {
    if (onNextClick === undefined || !progress) return;

    loadingData.current = true;
    onNextClick();
    setProgress(false);
  }, [progress]);

  return (
    <>
      <Latex>{assignment.assignment?.assignment?.assignmentText ?? ""}</Latex>

      {isShared ? (
        <div className="mb-3 mt-20px question">
          <AnswerTutoring
            index={0}
            assignment={assignment.assignment}
            type={"success"}
            userAssignmentId={assignment.id}
            subject={undefined}
            timeToLearn={timeToLearn}
          >
            {getTextForSelfEvaluationOption("success")}
          </AnswerTutoring>
          <SuccessRate successRate={assignment.assignment?.successRate} />
        </div>
      ) : (
        <>
          {assignmentResult?.selfEvalAnswers.map((value, index) => {
            const question = assignment.assignment?.assignment?.questions[index];
            const imageUrl = useAssetUrlResolver(question?.selfEvaluation?.general?.image);

            return (
              <div key={index}>
                <Latex>{question?.heading ?? ""}</Latex>
                {imageUrl !== null ? <img src={imageUrl} /> : null}

                <div className="mt-20px question">
                  <AnswerTutoring
                    index={index}
                    assignment={assignment.assignment}
                    type={"success"}
                    userAssignmentId={assignment.id}
                    subject={undefined}
                    timeToLearn={timeToLearn}
                  >
                    {getTextForSelfEvaluationOption("success")}
                  </AnswerTutoring>
                </div>
              </div>
            );
          })}

          {typeof assignment.assignment?.successRate === "number" ? (
            <div className="question">
              <SuccessRate successRate={assignment.assignment?.successRate} withHr={false} />
            </div>
          ) : null}
        </>
      )}
      <div className="exercise__footer">
        <div className="mw-sm">
          {onNextClick !== undefined ? (
            <Button className={"w-100 text-uppercase"} onClick={handleNextClick} disabled={loadingData.current}>
              {t("continue", { ns: "common" })}
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default SelfEvaluationAnalysis;
